import {
  FunctionComponent,
  useEffect,
  useMemo,
  useState,
  useRef,
  RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import './GlobalFilter.css';

import { useAuth } from '../../utility/useAuth';
import { useStoreActions, useStoreState } from '../../store';
import {
  getWarehouses,
  getOperationFilters,
} from '../../services/ApiService';

import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import ARROW_SRC from '../../Icons/Swagelok arrow.svg';
import { OperationFilter } from '../../types/OperationFilter';
import { todayRange, thisWeekRange } from '../../utility/dateFilter';
import { logger } from '../../utility/logger';
import {
  addUnique,
  removeValue,
  parseSearchValue,
  readDuePreset,
  jobMatchesText,
} from '../../utility/jobFilters';
import FilterAssist, { AppliableFilter, AssistApplied } from './FilterAssist';
import { describePlan, planBoardFilter } from '../../utility/filterIntent';
import { useSmartSearchSlot } from '../../utility/searchMode';

/**
 * Everything the bar filters on, as one comparable string. The assist box compares
 * it against the signature of the filters it applied, and hides its "Showing …"
 * line once they differ — a chip's X, Clear filters or a picker has made that
 * sentence untrue.
 */
const filterSignature = (f: {
  warehouses: string[];
  resources: string[];
  bpNames: string[];
  searchValue: string | undefined;
}) =>
  JSON.stringify([f.warehouses, f.resources, f.bpNames, f.searchValue ?? '']);

/* ---------- Filter chip ---------- */
const FilterChip = ({
  label,
  value,
  onClear,
}: {
  label: string;
  value: string;
  onClear: () => void;
}) => (
  <div className="gf-chip">
    <button
      className="gf-chip-x"
      aria-label={`Remove ${label} filter ${value}`}
      onClick={onClear}
    />
    <div className="gf-chip-text">
      <div className="gf-chip-heading">{label}</div>
      <div className="gf-chip-value">{value}</div>
    </div>
  </div>
);

const GlobalFilter: FunctionComponent = () => {
  const auth = useAuth();
  const { jobs, searchValue } = useStoreState(s => s.appSettings);

  const {
    setWarehouses,
    setOperationFilters,
    setBpNames,
    setSearchValue,
  } = useStoreActions(a => a.appSettings);

  /* ---------- dropdown data (every option the pickers can offer) ---------- */
  const [warehouseOptions, setWarehouseOptions] = useState<string[]>([]);
  const [operationOptions, setOperationOptions] = useState<OperationFilter[]>([]);
  const [bpOptions, setBpOptions] = useState<string[]>([]);

  /* ---------- selected filters ----------
     Warehouse / resource filter / BP name are all multi-select: each pick is
     added to the list and shown as its own removable chip, so an operator can
     watch two warehouses side by side. The pickers themselves stay
     single-select (pick one at a time) and reset to their placeholder after
     every pick — that is the interaction the shop floor asked for.          */
  const selectedWarehouses = useStoreState(s => s.appSettings.warehouses);
  const selectedOperations = useStoreState(s => s.appSettings.operationFilters);
  const selectedBps = useStoreState(s => s.appSettings.bpNames);

  const warehouseRef = useRef<DropDownListComponent>(null);
  const operationRef = useRef<DropDownListComponent>(null);
  const bpRef = useRef<DropDownListComponent>(null);

  const [duePreset, setDuePreset] =
    useState<'TODAY' | 'THIS_WEEK' | null>(null);
  const [dateFrom, setDateFrom] = useState<string | null>(null);
  const [dateTo, setDateTo] = useState<string | null>(null);

  const [calendarOpen, setCalendarOpen] = useState(false);
  const dueAnchorRef = useRef<HTMLDivElement | null>(null);

  /* ---------- load data ---------- */
  useEffect(() => {
    // A failure has to leave the option list empty (= an unfiltered board)
    // rather than reject unhandled.
    getWarehouses(auth)
      .then(setWarehouseOptions)
      .catch(error => logger.error('Failed to load warehouses', error));
    getOperationFilters(auth).then(setOperationOptions);
  }, [auth]);

  useEffect(() => {
    const names = Array.from(
      new Set(jobs.map(j => j?.customerName).filter(Boolean))
    ).sort();
    setBpOptions(names);
  }, [jobs]);

  /* ---------- no warehouse default ----------
     The bar used to chip the signed-in user's own warehouse
     (`Users.PrintLocation`) on first paint. It is deliberately gone: the board
     opens on every warehouse this tenant has and stays there until the
     operator picks one. Don't reintroduce it. */

  /* ---------- due-date state is DERIVED from the store ----------
     The range itself lives in the store (`searchValue`); the chip and the
     Today/This Week highlight are local, so they have to be read back from the
     store rather than assumed. This bar unmounts whenever you leave /home (and
     whenever the panel is collapsed), so on every mount:

       • a range already in the store is adopted as-is — a custom range, or
         Today, survives a trip to another page instead of being stomped by the
         default;
       • no range ⇒ apply the This Week default the shop floor expects.

     It used to unconditionally re-apply This Week on mount, which raced the
     board: the board's own mount effect, still holding the empty `searchValue`
     it had rendered with, cleared the store again — leaving the This Week chip
     lit over an unfiltered board.

     Clearing the range anywhere else (the chip's X, the panel's Hide toggle, a
     free-text search) now clears the chip with it, so the bar can never
     advertise a filter the board isn't applying. `hydratedRef` keeps that from
     bouncing straight back to the default: the default is a first-paint
     decision, not a "the range is empty" one — so once unchecked, it stays
     unchecked until you leave and come back. */
  const hydratedRef = useRef(false);

  useEffect(() => {
    const { from, to, text } = parseSearchValue(searchValue);

    if (from && to) {
      hydratedRef.current = true;
      setDateFrom(from);
      setDateTo(to);
      setDuePreset(readDuePreset(searchValue));
      return;
    }

    /* A free-text search in the store and no range: the operator searched, then
       left the board and came back. The default must NOT be applied over it —
       `composeSearchValue` writes the range alone, so it would delete the search
       term (the two share `searchValue`), and applying a week window to somebody
       hunting one order is how a job that exists reads as "no records found".
       Searching deliberately clears the range, so there is no chip to show. */
    if (text) {
      hydratedRef.current = true;
      setDateFrom(null);
      setDateTo(null);
      setDuePreset(null);
      return;
    }

    if (!hydratedRef.current) {
      hydratedRef.current = true;
      applyThisWeek();
      return;
    }

    // Cleared elsewhere — drop the chip rather than keep showing it.
    setDateFrom(null);
    setDateTo(null);
    setDuePreset(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  /**
   * Put a picker back on its placeholder so the next value can be added on top
   * of the ones already chipped. Deferred to the next tick because Syncfusion
   * is still inside its own `change` handler here and would re-apply the value
   * we just cleared.
   */
  const resetPicker = (ref: RefObject<DropDownListComponent>) => {
    setTimeout(() => {
      const picker = ref.current;
      if (picker) picker.value = null as any;
    }, 0);
  };

  /* ---------- search composition (date only) ----------
     BP name used to be smuggled through `searchValue` as free text. It is a
     first-class multi-select filter now (`bpNames` in the store), so this only
     carries the due-date range and its preset marker.                        */
const composeSearchValue = (opts: {
  from?: string | null;
  to?: string | null;
  preset?: string | null;
}) => {
  const parts: string[] = [];

  if (opts.preset) {
    parts.push(`__preset__:${opts.preset}`);
  }

  if (opts.from && opts.to) {
    parts.push(`__date__:${opts.from}:${opts.to}`);
  }

  setSearchValue(parts.join('|'));
};

  const isManagerMode = useStoreState(
   (s) => s.appSettings.isManagerMode
  );
  /* ---------- date helpers ---------- */
  const applyToday = () => {
    // Local calendar day — NOT toISOString()/UTC, which shifts a day forward
    // in the evening for users west of UTC and drops today's jobs.
    const [from, to] = todayRange();

    setDateFrom(from);
    setDateTo(to);
    setDuePreset('TODAY');
    setCalendarOpen(false);

    composeSearchValue({ from, to, preset: 'TODAY' });
  };

const applyThisWeek = () => {
  const [from, to] = thisWeekRange();

  setDateFrom(from);
  setDateTo(to);
  setDuePreset('THIS_WEEK');
  setCalendarOpen(false);

  composeSearchValue({ from, to, preset: 'THIS_WEEK' });
};



  const applyCustomDates = () => {
    if (dateFrom && dateTo) {
      setDuePreset(null);
      setCalendarOpen(false);
      composeSearchValue({ from: dateFrom, to: dateTo });
    }
  };

  const clearDates = () => {
    setDateFrom(null);
    setDateTo(null);
    setDuePreset(null);
    setCalendarOpen(false);
    composeSearchValue({});
  };

  const clearAllFilters = () => {
    clearDates();

    setWarehouses([]);
    setOperationFilters([]);
    setBpNames([]);
    setSearchValue('');
  };

  /* ---------- "describe what to show" ----------
     The assist box proposes a set of filters; this applies them, and everything
     it applies is an ordinary chip the operator can read and remove.

     ⚠️ IT GOES THROUGH THIS COMPONENT'S OWN SETTERS, NOT THE STORE DIRECTLY, and
     that is the same ownership rule the due-date state above documents: the bar
     owns the filters and the boards only read them. A second writer of
     `searchValue` is exactly what produced the "week filter comes back checked
     but unfiltered" race.

     ⚠️ AND `searchValue` IS ONLY WRITTEN WHEN THE PLAN SAYS SO. `undefined` from
     the planner means the request never mentioned dates or text, and writing the
     empty string there would clear the due-date range — opening the whole board
     under somebody who only asked to see one warehouse. */
  const applyAssistedFilter = (result: AppliableFilter): AssistApplied => {
    const plan = planBoardFilter(
      result,
      {
        warehouses: warehouseOptions,
        resources: operationOptions.map(o => o.name),
        bpNames: bpOptions,
      },
      {
        warehouses: selectedWarehouses,
        resources: selectedOperations.map(o => o.name),
        bpNames: selectedBps,
        searchValue,
      }
    );

    setWarehouses(plan.warehouses);
    setBpNames(plan.bpNames);

    // Resource filters are objects on the board (the name is what picks the
    // columns), so the planned NAMES are resolved back through the loaded
    // options — a name with no option behind it is dropped rather than turned
    // into a half-built filter the board cannot use.
    const resources = plan.resources
      .map(name => operationOptions.find(o => o.name === name))
      .filter((o): o is OperationFilter => !!o);
    setOperationFilters(resources);

    if (plan.searchValue !== undefined) setSearchValue(plan.searchValue);

    // Built from exactly what was just written, so it matches the store's
    // signature once these setters land and stops matching when anything else
    // changes a filter.
    return {
      message: describePlan(plan),
      signature: filterSignature({
        warehouses: plan.warehouses,
        resources: resources.map(o => o.name),
        bpNames: plan.bpNames,
        searchValue: plan.searchValue ?? searchValue,
      }),
    };
  };

  const formatDueLabel = () => {
    if (duePreset === 'TODAY') return 'Today';
    if (duePreset === 'THIS_WEEK') return 'This Week';
    if (dateFrom && dateTo) return `${dateFrom} – ${dateTo}`;
    return 'Select due date from/to';
  };

  const hasAnyFilter =
    selectedWarehouses.length > 0 ||
    selectedOperations.length > 0 ||
    selectedBps.length > 0 ||
    !!dateFrom;

  /* Options already chipped are dropped from their picker — re-picking one is
     a no-op, and hiding it makes it obvious what is left to add. Memoised so
     an unrelated re-render doesn't hand Syncfusion a new dataSource array and
     rebuild the popup underneath the operator. */
  const availableWarehouses = useMemo(
    () => warehouseOptions.filter(w => !selectedWarehouses.includes(w)),
    [warehouseOptions, selectedWarehouses]
  );
  const availableOperations = useMemo(
    () =>
      operationOptions.filter(
        o => !selectedOperations.some(s => s.name === o.name)
      ),
    [operationOptions, selectedOperations]
  );
  const availableBps = useMemo(
    () => bpOptions.filter(b => !selectedBps.includes(b)),
    [bpOptions, selectedBps]
  );

  /* ---------- the board's one search field ----------
     Where CoSMo can run, this box IS the search field (utility/searchMode.ts),
     portalled into the search row where the plain box would be; the bar still
     renders it because the bar owns the filters it sets. No slot (the bar mounted
     without the search row) ⇒ inline, as before. */
  const smartSlot = useSmartSearchSlot();

  /* Plain search first: text a plain search would find on the board (an order
     number, a stock code, a customer) is searched exactly as the plain box searches
     it — the same `setSearchValue(text)`, which replaces the due-date range just as
     a plain search does — and stays in the box to read and clear. Only text that
     matches nothing goes to CoSMo. Same rule as the boards: `jobMatchesText` in
     utility/jobFilters.ts. */
  const tryBasicSearch = (text: string): boolean => {
    if (!jobs.some(j => j && jobMatchesText(j, text))) return false;
    setSearchValue(text);
    return true;
  };

  const assist = (
    <FilterAssist
      options={{
        warehouses: warehouseOptions,
        resources: operationOptions.map(o => o.name),
        bpNames: bpOptions,
      }}
      onApply={applyAssistedFilter}
      tryBasicSearch={tryBasicSearch}
      activeSearchText={parseSearchValue(searchValue).text ?? ''}
      // The plain box's own "emptied ⇒ cleared": text and a due-date range never
      // coexist in searchValue, so there is no range for this to drop.
      onClearSearch={() => setSearchValue('')}
      filterSignature={filterSignature({
        warehouses: selectedWarehouses,
        resources: selectedOperations.map(o => o.name),
        bpNames: selectedBps,
        searchValue,
      })}
    />
  );

  /* ---------- render ---------- */
  return (
    <div className="gf-row">
      <div className="gf-left">
        {/* Renders nothing at all where the assistant feature is switched off, or
            where this region's Function App has no model deployment — see
            FilterAssist. */}
        {smartSlot ? createPortal(assist, smartSlot) : assist}

        {/* Warehouse */}
        <div className="gf-pair gf-overlay" data-csm-step="filter.warehouse">
          <DropDownListComponent
            ref={warehouseRef}
            cssClass="gf-dropdown"
            dataSource={availableWarehouses}
            value={null}
            placeholder="Warehouse"
            width = "10px"
            change={e => {
              if (!e.value) return;
              setWarehouses(addUnique(selectedWarehouses, e.value as string));
              resetPicker(warehouseRef);
            }}
          />
          <img src={ARROW_SRC} alt="" className='gf-arrow-overlay' />
        </div>

        {/* Operation */}
{!isManagerMode && (
  <div className="gf-pair gf-overlay" data-csm-step="filter.resource">
    <DropDownListComponent
      ref={operationRef}
      cssClass="gf-dropdown"
      dataSource={availableOperations}
      fields={{ text: 'name', value: 'name' }}
      value={null}
      placeholder="Select resource filter"
      width={2}
      change={e => {
        if (!e.value) return;
        const selected = operationOptions.find(o => o.name === e.value);
        if (selected && !selectedOperations.some(s => s.name === selected.name)) {
          setOperationFilters([...selectedOperations, selected]);
        }
        resetPicker(operationRef);
      }}
    />
    <img src={ARROW_SRC} alt="" className="gf-arrow-overlay" />
  </div>
)}

        {/* BP */}
        <div className="gf-pair gf-overlay" data-csm-step="filter.bp">
          <DropDownListComponent
            ref={bpRef}
            cssClass="gf-dropdown"
            dataSource={availableBps}
            value={null}
            placeholder="Select BP name"
            change={e => {
              if (!e.value) return;
              setBpNames(addUnique(selectedBps, e.value as string));
              resetPicker(bpRef);
            }}
          />
          <img src={ARROW_SRC} alt="" className='gf-arrow-overlay'  />
        </div>

        {/* Due date */}
{!isManagerMode && (
        <div className="gf-pair gf-due-anchor" data-csm-step="filter.due" ref={dueAnchorRef}>
          <div className="gf-due-label">
            Select due date from/to
            <br />
            <button
              className={`gf-due-chip ${duePreset === 'TODAY' ? 'is-active' : ''}`}
              onClick={applyToday}
            >
              Today
            </button>
            <span className="gf-due-sep"> | </span>
            <button
              className={`gf-due-chip ${duePreset === 'THIS_WEEK' ? 'is-active' : ''}`}
              onClick={applyThisWeek}
            >
              This Week
            </button>
          </div>

          <img
            src={ARROW_SRC}
            className="gf-arrow-btn"
            alt=""
            onClick={() => setCalendarOpen(o => !o)}
          />

          {calendarOpen && (
            <div className="gf-calendar">
              <label>
                From
                <input
                  type="date"
                  value={dateFrom ?? ''}
                  onChange={e => setDateFrom(e.target.value)}
                />
              </label>

              <label>
                To
                <input
                  type="date"
                  value={dateTo ?? ''}
                  onChange={e => setDateTo(e.target.value)}
                />
              </label>

              <div className="gf-calendar-actions">
                <button className="gf-btn gf-btn-cancel" onClick={clearDates}>
                  Cancel
                </button>
                <button className="gf-btn gf-btn-apply" onClick={applyCustomDates}>
                  OK
                </button>
              </div>
            </div>
          )}
        </div>
        )}

        {/* Selected filter chips — one per pick, each removable on its own. */}
        <div className="gf-chips" data-csm-step="filter.chips">
          {selectedWarehouses.map(w => (
            <FilterChip
              key={`wh-${w}`}
              label="Warehouse"
              value={w}
              onClear={() => setWarehouses(removeValue(selectedWarehouses, w))}
            />
          ))}

          {selectedOperations.map(o => (
            <FilterChip
              key={`op-${o.name}`}
              label="Operation"
              value={o.name}
              onClear={() =>
                setOperationFilters(
                  selectedOperations.filter(s => s.name !== o.name)
                )
              }
            />
          ))}

          {selectedBps.map(b => (
            <FilterChip
              key={`bp-${b}`}
              label="BP Name"
              value={b}
              onClear={() => setBpNames(removeValue(selectedBps, b))}
            />
          ))}

          {dateFrom && dateTo && (
            <FilterChip
              label="Due date from/to"
              value={formatDueLabel()}
              onClear={clearDates}
            />
          )}
        </div>
      </div>

      {/* RIGHT: only the Clear filters action */}
      <div className="gf-right">
        {hasAnyFilter && (
          <button className="gf-clear" data-csm-step="filter.clear" onClick={clearAllFilters}>
            <span className="gf-clear-icon" />
            <span className="gf-clear-text">Clear filters</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default GlobalFilter;
